import { db, getMeta, META_KEYS } from "../../core/data/local/db";
import { uuidv7 } from "../../core/platform/id";
import type {
  SaleFinalizedEvent,
  SaleRefundedEvent,
  SaleVoidedEvent,
} from "../../core/domain/events/bus";
import { loadReplica } from "./replica";
import { indexRecipes, resolveLine } from "./resolve-recipe";
import {
  buildSaleDeduction,
  buildVoidReversal,
  buildRefundReversal,
  deductionRecordKey,
  type DeductionRecord,
  type MovementPayload,
} from "./deduction";

/**
 * What the terminal writes when a sale is rung, voided, or refunded
 * (contracts/inventory-sync.md "What the terminal does").
 *
 * AFTER THE SALE, NEVER INSIDE IT. `commitSale()` emits `sale.finalized` only
 * once the sale is durable, so nothing here can roll a sale back. The envelope
 * and its deduction record commit together in a transaction of their own; a
 * crash in the milliseconds between loses a deduction, never a sale, and a
 * stock count corrects it (FR-118).
 *
 * EVERY FAILURE STOPS HERE. The event bus already isolates a throwing
 * subscriber, but this module does not lean on that: a sale is the one thing
 * that must never notice inventory exists, so each handler catches, logs, and
 * returns.
 */

const MODULE_ID = "inventory";
const ENVELOPE_TYPE = "inventory.movement";

interface RecordToKeep {
  saleId: string;
  shiftId: string;
  record: DeductionRecord;
}

async function enqueue(payload: MovementPayload, terminalId: string, keep?: RecordToKeep): Promise<void> {
  // A fresh op_id, never the sale's: the sale's sync_operation row would
  // swallow this as a replay of itself.
  const opId = uuidv7();

  await db.transaction("rw", db.outbox, db.moduleRecords, async () => {
    await db.outbox.put({
      opId,
      type: ENVELOPE_TYPE,
      payload,
      createdAt: payload.createdAt,
      terminalId,
      attempts: 0,
      state: "pending",
      lastError: null,
      nextAttemptAt: null,
    });

    if (keep) {
      await db.moduleRecords.put({
        opId: deductionRecordKey(keep.saleId),
        moduleId: MODULE_ID,
        kind: "deduction",
        shiftId: keep.shiftId,
        record: keep.record,
        createdAt: payload.createdAt,
      });
    }
  });
}

async function thisTerminal(): Promise<string> {
  return (await getMeta<string>(META_KEYS.terminalId)) ?? "";
}

export async function onSaleFinalized(event: SaleFinalizedEvent): Promise<void> {
  try {
    const [replica, storedLines] = await Promise.all([
      loadReplica(),
      db.saleLines.where("saleId").equals(event.saleId).toArray(),
    ]);

    // The event carries ids; the unmapped list needs the name the customer saw.
    const names = new Map(storedLines.map((line) => [line.id, line.itemNameSnapshot]));
    const index = indexRecipes(replica.recipes);

    const { payload, record } = buildSaleDeduction(
      {
        saleId: event.saleId,
        staffId: event.staffId,
        createdAt: event.createdAt,
        lines: event.lines.map((line) =>
          resolveLine(index, {
            lineId: line.lineId,
            menuItemId: line.menuItemId,
            variantId: line.variantId,
            modifierIds: line.modifierIds,
            quantity: line.quantity,
            itemName: names.get(line.lineId) ?? line.menuItemId,
          }),
        ),
      },
      uuidv7,
    );

    if (!payload) return;

    await enqueue(
      payload,
      event.terminalId,
      record ? { saleId: event.saleId, shiftId: event.shiftId, record } : undefined,
    );
  } catch (error) {
    console.error(`[inventory] could not record the deduction for sale ${event.saleId}`, error);
  }
}

/** What the sale took, or null when it took nothing — then nothing is given back. */
async function deductionFor(saleId: string): Promise<DeductionRecord | null> {
  const row = await db.moduleRecords.get(deductionRecordKey(saleId));
  return row?.moduleId === MODULE_ID ? (row.record as DeductionRecord) : null;
}

export async function onSaleVoided(event: SaleVoidedEvent): Promise<void> {
  try {
    const record = await deductionFor(event.saleId);
    if (!record) return;

    const payload = buildVoidReversal(
      record,
      { staffId: event.staffId, createdAt: event.createdAt },
      uuidv7,
    );
    if (payload) await enqueue(payload, await thisTerminal());
  } catch (error) {
    console.error(`[inventory] could not reverse the deduction for voided sale ${event.saleId}`, error);
  }
}

export async function onSaleRefunded(event: SaleRefundedEvent): Promise<void> {
  try {
    const record = await deductionFor(event.saleId);
    if (!record) return;

    const payload = buildRefundReversal(
      record,
      event.lines,
      { staffId: event.staffId, createdAt: event.createdAt },
      uuidv7,
    );
    if (payload) await enqueue(payload, await thisTerminal());
  } catch (error) {
    console.error(`[inventory] could not reverse the deduction for refunded sale ${event.saleId}`, error);
  }
}
